import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { enviroment } from 'src/environments/environment';

import {
  AdjustTablesIndexResponse,
  EditorOptions,
  EditorResponse,
} from '../model/response/adjust-editor';
import {
  FtFileType,
  InjectionMap,
  MapEnvelope,
} from '../model/response/file-map-response';
import { MapPasswordService } from '../share/map-password/map-password.service';

export interface FileMapError {
  errorCode: string;
  message: string;
  status: number;
  /** `context.reason` das consultas de ajuste (ex.: SOURCE_HASH_MISMATCH); ausente nas demais. */
  reason?: string;
}

/**
 * Endpoint POST /user/{userID}/file/{fileID}/map (proxy do `pipeline-read-log`,
 * resource "map_log"). Aceita os 4 tipos: ftml, ftlog (log+mapa) e ftm, ftmap
 * (mapa standalone, sem log).
 *
 * Para arquivos de log, esta chamada **também dispara a conversão do log para
 * parquet** quando feita pela primeira vez — ou seja, é a chamada
 * "preparatória" antes de pedir canais (`FileChannelsService.read`) num
 * arquivo nunca acessado.
 *
 * Contrato: docs/Contrato técnico de mapas tabelas e logs para o front.md (§10 para o
 * `map_log`; §3 a §9 para as consultas de ajuste).
 *
 * Mapas com ajustes protegidos respondem 422 `MAP_PASSWORD_PROTECTED`; o
 * `MapPasswordService` intercepta aqui (único call-site do `/map`), pede a
 * senha num modal global e repete a leitura com `password` cifrada — todos
 * os chamadores (`read`, `readMaps`, `warmupParquet`) ganham isso de graça.
 */
@Injectable({ providedIn: 'root' })
export class FileMapService {
  constructor(
    private http: HttpClient,
    private passwordGate: MapPasswordService,
  ) {}

  /**
   * Lê o envelope completo `{ maps, log, compensations, errors }`. Pode
   * retornar HTTP 200 mesmo com falha parcial (qualquer parte null); o
   * chamador deve checar `errors.map` / `errors.log` / `errors.compensations`
   * quando a parte correspondente vier nula.
   */
  read(userID: string, fileID: string, fileType: FtFileType): Observable<MapEnvelope> {
    const url = `${enviroment.BASE_URL_API}/user/${userID}/file/${fileID}/map`;
    return this.passwordGate.guard(`${userID}|${fileID}`, fileType, (password) =>
      this.http
        .post<MapEnvelope>(url, password ? { file_type: fileType, password } : { file_type: fileType })
        .pipe(catchError((err: HttpErrorResponse) => throwError(() => this.toError(err)))),
    );
  }

  /** Índice das tabelas do ajuste (`adjust_tables` com `index:true`). */
  listTables(
    userID: string,
    fileID: string,
    fileType: FtFileType,
    editorOptions: EditorOptions,
  ): Observable<AdjustTablesIndexResponse> {
    return this.postAdjust<AdjustTablesIndexResponse>(userID, fileID, fileType, {
      resource: 'adjust_tables',
      index: true,
      editor_options: editorOptions,
    });
  }

  /** Lê uma tabela no formato do editor do software (`adjust_editor`). */
  readTable(
    userID: string,
    fileID: string,
    fileType: FtFileType,
    tableId: string,
    editorOptions: EditorOptions,
  ): Observable<EditorResponse> {
    return this.postAdjust<EditorResponse>(userID, fileID, fileType, {
      resource: 'adjust_editor',
      table_id: tableId,
      editor_options: editorOptions,
    });
  }

  /**
   * Pede a prévia de uma edição (não grava o arquivo). Envia a matriz inteira, na ordem
   * exibida, e o `source_hash` (= `source.content_sha256` da leitura atual).
   */
  previewTable(
    userID: string,
    fileID: string,
    fileType: FtFileType,
    tableId: string,
    editorOptions: EditorOptions,
    sourceHash: string,
    editedValues: number[][],
  ): Observable<EditorResponse> {
    return this.postAdjust<EditorResponse>(userID, fileID, fileType, {
      resource: 'adjust_editor',
      table_id: tableId,
      editor_options: editorOptions,
      source_hash: sourceHash,
      edited_values: editedValues,
    });
  }

  /**
   * Conveniência: retorna só os `InjectionMap[]` (1 item em PowerFT, até 3
   * bancos em Vision), levantando erro se vier nulo/vazio.
   */
  readMaps(userID: string, fileID: string, fileType: FtFileType): Observable<InjectionMap[]> {
    return this.read(userID, fileID, fileType).pipe(
      map((env) => {
        if (!env.maps || env.maps.length === 0) {
          throw new Error(env.errors?.map?.message ?? 'mapa indisponível');
        }
        return env.maps;
      }),
    );
  }

  /**
   * Warmup do parquet: chama `/map` apenas para garantir que a conversão
   * aconteça antes de uma chamada subsequente a `/channels`. Ignora falha
   * parcial — o que importa é o HTTP 200 (significa que o parquet já está
   * pronto via `log.ready`, OU o map falhou mas o log foi convertido OK).
   * Sem sentido para `.ftm`/`.ftmap` (mapa standalone não tem log).
   */
  warmupParquet(userID: string, fileID: string, fileType: FtFileType): Observable<void> {
    return this.read(userID, fileID, fileType).pipe(map(() => void 0));
  }

  /**
   * POST do `/map` para os recursos de ajuste. O corpo vai sem `user_id`/`source_file_id`
   * (vêm da rota); passa pela mesma guarda de senha do `read()`.
   */
  private postAdjust<T>(
    userID: string,
    fileID: string,
    fileType: FtFileType,
    body: Record<string, unknown>,
  ): Observable<T> {
    const url = `${enviroment.BASE_URL_API}/user/${userID}/file/${fileID}/map`;
    return this.passwordGate.guard(`${userID}|${fileID}`, fileType, (password) =>
      this.http
        .post<T>(url, { file_type: fileType, ...body, ...(password ? { password } : {}) })
        .pipe(catchError((err: HttpErrorResponse) => throwError(() => this.toError(err)))),
    );
  }

  private toError(err: HttpErrorResponse): FileMapError {
    let errorCode = 'NETWORK_ERROR';
    let message = err.message ?? 'Erro de rede.';
    let reason: string | undefined;
    if (typeof err.error === 'object' && err.error?.error_code) {
      errorCode = err.error.error_code;
      message = err.error.message ?? message;
      reason = err.error.context?.reason;
    }
    return reason
      ? { errorCode, message, status: err.status, reason }
      : { errorCode, message, status: err.status };
  }
}
